// frontend/src/components/tax/TaxFieldTable.tsx
//
// TAX-PARSE-2 — table of extracted fields for a single tax document.
// Columns: Field | Value | Confidence | Source | Status.
//
// `needs_verification` fields get an amber "Needs verification" status pill —
// this is informational, NOT an auto-flag (that judgment belongs to the
// cross-verifier / CPA review, per the engine-integrity gate in the brief).

import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table'
import type { TaxDocumentResponse } from '@/api/tax'

function fmtFieldValue(value: unknown): string {
  if (value == null) return '—'
  if (typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value)
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

function fmtFieldName(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const pct = Math.round(confidence * 100)
  const styles =
    confidence >= 0.85
      ? 'bg-green-50 text-green-800 border-green-200'
      : confidence >= 0.6
        ? 'bg-amber-50 text-amber-800 border-amber-200'
        : 'bg-red-50 text-red-800 border-red-200'
  return (
    <span
      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold tracking-wide ${styles}`}
    >
      {pct}%
    </span>
  )
}

interface TaxFieldTableProps {
  document: TaxDocumentResponse
}

export function TaxFieldTable({ document }: TaxFieldTableProps) {
  const entries = Object.entries(document.fields ?? {})

  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No fields were extracted from this document.
      </p>
    )
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Field</TableHead>
            <TableHead>Value</TableHead>
            <TableHead>Confidence</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map(([key, value]) => (
            <TableRow key={key}>
              <TableCell className="font-medium">{fmtFieldName(key)}</TableCell>
              <TableCell className="tabular-nums">{fmtFieldValue(value)}</TableCell>
              <TableCell>
                <ConfidenceBadge confidence={document.confidence} />
              </TableCell>
              <TableCell className="text-muted-foreground text-xs whitespace-normal">
                {document.source_document} · {document.form_type.toUpperCase()}
                {document.tax_year ? ` · FY${document.tax_year}` : ''}
              </TableCell>
              <TableCell>
                {document.needs_verification ? (
                  <span className="inline-flex items-center rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                    Needs verification
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-800">
                    Verified
                  </span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default TaxFieldTable
